<template>
  <div class="video-player-wrapper">
    <!-- Thumbnail with Play Button (before playing) -->
    <!-- Show if we have thumbnail OR if we have original video (for PROCESSING status) -->
    <div
      v-if="!isPlaying && (thumbnailUrl || (status === 'PROCESSING' && originalVideoUrl))"
      class="video-thumbnail-container"
      :style="{ width: width, height: height }"
      @click="playVideo"
    >
      <img v-if="thumbnailUrl" :src="thumbnailUrl" alt="Video thumbnail" class="video-thumbnail" />
      <div v-else class="video-thumbnail-placeholder">
        <a-icon type="video-camera" :style="{ fontSize: '64px', color: '#999' }" />
      </div>
      <div class="play-button-overlay">
        <div class="play-button">
          <a-icon type="play-circle" :style="{ fontSize: '64px', color: '#fff' }" />
        </div>
      </div>
      <!-- Processing Status Badge -->
      <div v-if="status === 'PROCESSING'" class="processing-badge">
        <a-spin size="small" />
        <span class="ml-2">{{ $t('actualite.videoProcessing') }}</span>
      </div>
      <div v-else-if="status === 'FAILED'" class="error-badge">
        <a-icon type="exclamation-circle" />
        <span class="ml-2">{{ $t('actualite.videoError') }}</span>
      </div>
    </div>

    <!-- Video Player (when playing - original or HLS) -->
    <div
      v-else-if="isPlaying && (hlsUrl || originalVideoUrl)"
      class="video-container"
      :style="{ width: width, height: height }"
      :class="{ 'is-fullscreen': isFullscreen }"
      @mouseenter="showControls = true"
      @mouseleave="showControls = false"
      @mousemove="resetControlsTimeout"
    >
      <video
        ref="videoElement"
        :width="width"
        :height="height"
        class="video-player"
        @ended="onVideoEnded"
        @pause="onVideoPause"
        @play="onVideoPlay"
        @timeupdate="onTimeUpdate"
        @loadedmetadata="onLoadedMetadata"
        @waiting="onVideoWaiting"
        @canplay="onVideoCanPlay"
        @playing="onVideoPlaying"
        @click="handleVideoClick"
        @dblclick="handleVideoDoubleClick"
      >
        {{ $t('actualite.videoTagNotSupported') }}
      </video>
      <!-- Processing Badge (when playing original during processing) -->
      <div v-if="status === 'PROCESSING' && isPlayingOriginal" class="processing-badge-overlay">
        <a-badge :status="'processing'" :text="$t('actualite.videoProcessing')" />
      </div>
      <!-- Loading Indicator -->
      <div v-if="isBuffering" class="video-loading-overlay">
        <a-spin size="large" />
        <p class="mt-2">{{ $t('actualite.videoLoading')  }}</p>
      </div>
      <a-button
        class="close-button"
        type="default"
        shape="circle"
        icon="close"
        @click="stopVideo"
      />
      <!-- Custom Video Controls -->
      <div class="video-controls" :class="{ 'controls-visible': showControls || !isPlaying }">
        <!-- Progress Bar -->
        <div class="controls-progress" @click="seekTo">
          <div class="progress-bar">
            <div class="progress-filled" :style="{ width: progressPercent + '%' }"></div>
            <div class="progress-buffer" :style="{ width: bufferedPercent + '%' }"></div>
          </div>
        </div>
        <!-- Controls Bar -->
        <div class="controls-bar">
          <div class="controls-left">
            <!-- Play/Pause Button -->
            <a-button
              class="control-button"
              type="link"
              @click="togglePlayPause"
            >
              <a-icon :type="isPaused ? 'play-circle' : 'pause-circle'" />
            </a-button>
            <!-- Volume Control -->
            <div class="volume-control">
              <a-button
                class="control-button"
                type="link"
                @click="toggleMute"
              >
                <i v-if="isMuted" class="fa fa-volume-off" style="font-size: 18px; color: #ff4d4f;"></i>
                <i v-else class="fa fa-volume-up" style="font-size: 18px;"></i>
              </a-button>
              <div class="volume-slider-wrapper">
                <input
                  type="range"
                  class="volume-slider"
                  min="0"
                  max="100"
                  :value="volume"
                  @input="setVolume"
                />
              </div>
            </div>
            <!-- Time Display -->
            <span class="time-display">{{ currentTimeFormatted }} / {{ durationFormatted }}</span>
          </div>
          <div class="controls-right">
            <!-- Quality Selector (only for HLS, not original video) -->
            <div v-if="!isPlayingOriginal && availableLevels.length > 1" class="quality-control">
              <a-dropdown 
                :trigger="['click']" 
                placement="topRight" 
                :getPopupContainer="getPopupContainer"
                :overlayClassName="isFullscreen ? 'quality-dropdown-fullscreen' : ''"
              >
                <a-button 
                  class="control-button quality-button" 
                  type="link"
                >
                  <a-icon type="setting" />
                  <span class="quality-label">{{ currentQualityLabel }}</span>
                </a-button>
                <a-menu slot="overlay" @click="handleQualityChange">
                  <a-menu-item key="auto" :class="{ 'ant-menu-item-selected': currentLevel === -1 }">
                    {{ $t('actualite.videoQualityAuto') }}
                  </a-menu-item>
                  <a-menu-item
                    v-for="(level, index) in availableLevels"
                    :key="index"
                    :class="{ 'ant-menu-item-selected': currentLevel === index }"
                  >
                    {{ getQualityLabel(level) }}
                  </a-menu-item>
                </a-menu>
              </a-dropdown>
            </div>
            <!-- Fullscreen Button -->
            <a-button
              class="control-button"
              type="link"
              @click="toggleFullscreen"
            >
              <a-icon :type="isFullscreen ? 'fullscreen-exit' : 'fullscreen'" />
            </a-button>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading State (only if no original video available) -->
    <div
      v-else-if="status === 'PROCESSING' && !thumbnailUrl && !originalVideoUrl"
      class="video-loading-container"
      :style="{ width: width, height: height }"
    >
      <a-spin size="large" />
      <p class="mt-3">{{ $t('actualite.videoProcessing') }}</p>
    </div>

    <!-- Error State -->
    <div
      v-else-if="status === 'FAILED'"
      class="video-error-container"
      :style="{ width: width, height: height }"
    >
      <a-icon type="exclamation-circle" :style="{ fontSize: '48px', color: '#ff4d4f' }" />
      <p class="mt-3">{{ $t('actualite.videoError') }}</p>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import Hls from 'hls.js';

export default {
  name: 'VideoPlayer',
  props: {
    // HLS master playlist URL
    hlsUrl: {
      type: String,
      required: false,
      default: null,
    },
    // Thumbnail URL
    thumbnailUrl: {
      type: String,
      required: false,
      default: null,
    },
    // Video status: PROCESSING, READY, FAILED
    status: {
      type: String,
      default: 'PROCESSING',
      validator: (value) => ['PROCESSING', 'READY', 'FAILED'].includes(value),
    },
    // Player dimensions
    width: {
      type: String,
      default: '100%',
    },
    height: {
      type: String,
      default: '400px',
    },
    // Auto-play (not recommended for HLS)
    autoplay: {
      type: Boolean,
      default: false,
    },
    // Resources URL pattern for signing S3 URLs (e.g., "https://cdn.example.com/FILE_PATH")
    resourcesUrl: {
      type: String,
      required: true,
    },
    // Available renditions from video object
    renditions: {
      type: Array,
      default: () => [],
    },
    // Original video key (for playing while processing)
    originalKey: {
      type: String,
      required: false,
      default: null,
    },
  },
  data() {
    return {
      isPlaying: false,
      hls: null,
      availableLevels: [],
      currentLevel: -1, // -1 means auto
      isFullscreen: false,
      isPaused: true,
      isMuted: false,
      volume: 100,
      currentTime: 0,
      duration: 0,
      buffered: 0,
      showControls: true,
      controlsTimeout: null,
      isBuffering: false,
    };
  },
  computed: {
    // Original video URL (signed)
    originalVideoUrl() {
      if (!this.originalKey) return null;
      return this.resourcesUrl.replace('FILE_PATH', this.originalKey);
    },
    // Check if we're playing the original video (not HLS)
    isPlayingOriginal() {
      return this.isPlaying && this.status === 'PROCESSING' && !this.hlsUrl && this.originalVideoUrl;
    },
    // Determine which video source to use (prefer HLS if ready, otherwise original)
    videoSource() {
      if (this.status === 'READY' && this.hlsUrl) {
        return 'hls';
      }
      if (this.status === 'PROCESSING' && this.originalVideoUrl) {
        return 'original';
      }
      return null;
    },
    currentQualityLabel() {
      if (this.currentLevel === -1) {
        return this.$t('actualite.videoQualityAuto');
      }
      if (this.availableLevels[this.currentLevel]) {
        return this.getQualityLabel(this.availableLevels[this.currentLevel]);
      }
      return this.$t('actualite.videoQualityAuto');
    },
    progressPercent() {
      if (!this.duration) return 0;
      return (this.currentTime / this.duration) * 100;
    },
    bufferedPercent() {
      if (!this.duration) return 0;
      return (this.buffered / this.duration) * 100;
    },
    currentTimeFormatted() {
      return this.formatTime(this.currentTime);
    },
    durationFormatted() {
      return this.formatTime(this.duration);
    },
  },
  mounted() {
    // Auto-play if requested (usually not recommended)
    // Only autoplay if HLS is ready, not for original videos
    if (this.autoplay && this.hlsUrl && this.status === 'READY') {
      this.playVideo();
    }
    
    // Check initial fullscreen state
    this.checkFullscreenState();
    
    // Set up global fullscreen listeners
    document.addEventListener('fullscreenchange', this.checkFullscreenState);
    document.addEventListener('webkitfullscreenchange', this.checkFullscreenState);
    document.addEventListener('mozfullscreenchange', this.checkFullscreenState);
    document.addEventListener('MSFullscreenChange', this.checkFullscreenState);
    
    // Set up keyboard listener for spacebar (play/pause)
    document.addEventListener('keydown', this.handleKeyDown);
  },
  beforeDestroy() {
    this.destroyHls();
    this.removeFullscreenListeners();
    
    // Remove global fullscreen listeners
    document.removeEventListener('fullscreenchange', this.checkFullscreenState);
    document.removeEventListener('webkitfullscreenchange', this.checkFullscreenState);
    document.removeEventListener('mozfullscreenchange', this.checkFullscreenState);
    document.removeEventListener('MSFullscreenChange', this.checkFullscreenState);
    
    // Remove keyboard listener
    document.removeEventListener('keydown', this.handleKeyDown);
  },
  watch: {
    // Watch for hlsUrl changes (when HLS becomes ready, switch from original to HLS)
    hlsUrl(newUrl, oldUrl) {
      if (this.isPlaying && newUrl && this.status === 'READY') {
        // If we were playing original and HLS is now ready, switch to HLS
        if (oldUrl === null && this.isPlayingOriginal) {
          this.loadHls();
        } else if (newUrl !== oldUrl) {
          this.loadHls();
        }
      }
    },
    // Watch for status changes (when status changes from PROCESSING to READY)
    status(newStatus, oldStatus) {
      if (this.isPlaying && oldStatus === 'PROCESSING' && newStatus === 'READY' && this.hlsUrl) {
        // Switch from original to HLS when processing completes
        this.loadHls();
      }
    },
  },
  methods: {
    playVideo() {
      // If HLS is ready, use it (preferred)
      if (this.status === 'READY' && this.hlsUrl) {
        this.isPlaying = true;
        this.isPaused = false;
        this.$nextTick(() => {
          this.loadHls();
          this.resetControlsTimeout();
          // Ensure video starts playing after HLS loads
          const video = this.$refs.videoElement;
          if (video) {
            // Wait a bit for HLS to initialize, then play
            setTimeout(() => {
              if (video && !video.paused) return; // Already playing
              video.play().catch((e) => {
                console.log('Play error:', e);
                // If autoplay fails, user can click play button
              });
            }, 500);
          }
        });
        return;
      }

      // If processing and original video is available, play it
      if (this.status === 'PROCESSING' && this.originalVideoUrl) {
        this.isPlaying = true;
        this.isPaused = false;
        this.$nextTick(() => {
          this.loadOriginalVideo();
          this.resetControlsTimeout();
        });
        return;
      }

      // No video available
      this.$message.warning(this.$t('actualite.videoNotReady'));
    },

    stopVideo() {
      this.isPlaying = false;
      this.isPaused = true;
      this.destroyHls();
      if (this.$refs.videoElement) {
        this.$refs.videoElement.pause();
        this.$refs.videoElement.src = '';
      }
      clearTimeout(this.controlsTimeout);
    },

    loadOriginalVideo() {
      const video = this.$refs.videoElement;
      if (!video || !this.originalVideoUrl) return;

      // Destroy any existing HLS instance
      this.destroyHls();

      // Load original MP4 video directly
      video.src = this.originalVideoUrl;
      video.load();

      // Setup fullscreen listeners
      this.$nextTick(() => {
        this.setupFullscreenListeners();
      });

      // Try to play
      video.play().catch((e) => {
        console.log('Play error:', e);
        this.isPaused = true;
      });
    },

    loadHls() {
      const video = this.$refs.videoElement;
      if (!video || !this.hlsUrl) return;

      // Destroy existing HLS instance
      this.destroyHls();

      // Extract S3 key from master playlist URL to determine base path
      // Master URL: https://cdn.example.com/school1/manager/actuality/videos/ID/hls/master.m3u8
      // We need to extract: school1/manager/actuality/videos/ID
      let baseS3Path = '';
      try {
        const masterUrlObj = new URL(this.hlsUrl);
        const masterPath = masterUrlObj.pathname;
        // Extract base path (everything before /hls/master.m3u8)
        const basePathMatch = masterPath.match(/^(.+)\/hls\/master\.m3u8$/);
        if (basePathMatch) {
          baseS3Path = basePathMatch[1].startsWith('/') ? basePathMatch[1].substring(1) : basePathMatch[1];
        }
      } catch (error) {
        console.error('Error parsing master URL:', error);
      }

      // Check if HLS is supported
      if (typeof Hls !== 'undefined' && Hls.isSupported()) {
        // Use HLS.js for browsers that support it
        this.hls = new Hls({
          enableWorker: true,
          lowLatencyMode: false,
          backBufferLength: 90,
          // Intercept all XHR requests to sign URLs
          xhrSetup: (xhr, url) => {
            try {
              let s3Key = '';
              
              // Check if URL is already absolute (signed URL from CloudFront)
              if (url.startsWith('http://') || url.startsWith('https://')) {
                // Extract S3 key from the absolute URL
                const urlObj = new URL(url);
                s3Key = urlObj.pathname.startsWith('/') ? urlObj.pathname.substring(1) : urlObj.pathname;
              } else {
                // Relative URL from playlist (e.g., "720p/stream.m3u8" or "720p/seg_00000.ts")
                // Construct full S3 key: baseS3Path + /hls/ + relativePath
                const relativePath = url.startsWith('/') ? url.substring(1) : url;
                s3Key = baseS3Path + '/hls/' + relativePath;
              }
              
              // Sign the URL using resourcesUrl pattern
              const signedUrl = this.resourcesUrl.replace('FILE_PATH', s3Key);
              
              // Override the URL with signed version
              xhr.open('GET', signedUrl, true);
            } catch (error) {
              console.error('Error signing HLS URL:', error, 'Original URL:', url);
              // Fallback to original URL if signing fails
              xhr.open('GET', url, true);
            }
          },
        });

        this.hls.loadSource(this.hlsUrl);
        this.hls.attachMedia(video);

        // Setup fullscreen listeners after video is attached
        this.$nextTick(() => {
          this.setupFullscreenListeners();
        });

        this.hls.on(Hls.Events.MANIFEST_PARSED, (event, data) => {
          // Video is ready to play
          // Update available levels
          this.availableLevels = data.levels || [];
          // Set current level to auto (-1) initially
          this.currentLevel = this.hls.currentLevel !== undefined ? this.hls.currentLevel : -1;
          
          // Always try to play when manifest is parsed (user clicked play)
          if (this.isPlaying && !this.isPaused) {
            video.play().catch((e) => {
              console.log('Play prevented:', e);
              // If play fails, set paused state
              this.isPaused = true;
            });
          }
          this.resetControlsTimeout();
        });

        // Listen for level switches (both manual and automatic)
        this.hls.on(Hls.Events.LEVEL_SWITCHED, (event, data) => {
          this.currentLevel = data.level;
        });

        // Listen for level loading to update current level
        this.hls.on(Hls.Events.LEVEL_LOADED, (event, data) => {
          // Update current level if it changed
          if (this.hls.currentLevel !== undefined) {
            this.currentLevel = this.hls.currentLevel;
          }
        });

        this.hls.on(Hls.Events.ERROR, (event, data) => {
          if (data.fatal) {
            switch (data.type) {
              case Hls.ErrorTypes.NETWORK_ERROR:
                console.error('Fatal network error encountered, try to recover');
                this.hls.startLoad();
                break;
              case Hls.ErrorTypes.MEDIA_ERROR:
                console.error('Fatal media error encountered, try to recover');
                this.hls.recoverMediaError();
                break;
              default:
                console.error('Fatal error, cannot recover');
                this.hls.destroy();
                this.$message.error(this.$t('actualite.videoPlaybackError'));
                break;
            }
          }
        });
             } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
               // Native HLS support (Safari)
               video.src = this.hlsUrl;
               
               // Setup fullscreen listeners for native HLS
               this.$nextTick(() => {
                 this.setupFullscreenListeners();
               });
               
               if (this.autoplay) {
                 video.play().catch((e) => {
                   console.log('Autoplay prevented:', e);
                 });
               }
               this.resetControlsTimeout();
             } else {
        this.$message.error(this.$t('actualite.videoNotSupported'));
      }
    },

    destroyHls() {
      if (this.hls) {
        this.hls.destroy();
        this.hls = null;
      }
      this.removeFullscreenListeners();
    },

    onVideoEnded() {
      // Optional: handle video end
      this.$emit('ended');
    },


    onVideoPlay() {
      this.isPaused = false;
      this.$emit('play');
    },

    onVideoPause() {
      this.isPaused = true;
      this.$emit('pause');
    },

    onTimeUpdate() {
      const video = this.$refs.videoElement;
      if (video) {
        this.currentTime = video.currentTime;
        this.duration = video.duration || this.duration;
        
        // Calculate buffered amount
        if (video.buffered && video.buffered.length > 0) {
          this.buffered = video.buffered.end(video.buffered.length - 1);
        }
      }
    },

    onLoadedMetadata() {
      const video = this.$refs.videoElement;
      if (video) {
        this.duration = video.duration;
        this.volume = Math.round(video.volume * 100);
        this.isMuted = video.muted;
      }
    },

    onVideoWaiting() {
      // Video is waiting for data (buffering)
      this.isBuffering = true;
    },

    onVideoCanPlay() {
      // Video has enough data to play
      this.isBuffering = false;
    },

    onVideoPlaying() {
      // Video is playing (not buffering)
      this.isBuffering = false;
      this.isPaused = false;
    },

    handleVideoClick(event) {
      // Don't toggle if clicking on controls, buttons, or dropdowns
      if (event.target.closest('.video-controls') || 
          event.target.closest('.close-button') ||
          event.target.closest('.quality-control') ||
          event.target.closest('.control-button') ||
          event.target.closest('.ant-dropdown') ||
          event.target.closest('.ant-menu')) {
        return;
      }
      
      // Toggle play/pause when clicking on video
      this.togglePlayPause();
    },

    handleVideoDoubleClick(event) {
      // Don't toggle fullscreen if double-clicking on controls, buttons, or dropdowns
      if (event.target.closest('.video-controls') || 
          event.target.closest('.close-button') ||
          event.target.closest('.quality-control') ||
          event.target.closest('.control-button') ||
          event.target.closest('.ant-dropdown') ||
          event.target.closest('.ant-menu')) {
        return;
      }
      
      // Prevent default double-click behavior (text selection, etc.)
      event.preventDefault();
      event.stopPropagation();
      
      // Toggle fullscreen when double-clicking on video
      this.toggleFullscreen();
    },

    handleKeyDown(event) {
      // Only handle spacebar when video is playing (not on thumbnail)
      if (!this.isPlaying || !this.hlsUrl) {
        return;
      }
      
      // Check if spacebar is pressed
      if (event.code === 'Space' || event.key === ' ' || event.keyCode === 32) {
        // Don't trigger if user is typing in an input field
        const activeElement = document.activeElement;
        if (activeElement && (
          activeElement.tagName === 'INPUT' ||
          activeElement.tagName === 'TEXTAREA' ||
          activeElement.isContentEditable
        )) {
          return;
        }
        
        // Prevent default behavior (page scroll)
        event.preventDefault();
        event.stopPropagation();
        
        // Toggle play/pause
        this.togglePlayPause();
      }
    },

    togglePlayPause() {
      const video = this.$refs.videoElement;
      if (!video) return;
      
      if (video.paused) {
        video.play();
      } else {
        video.pause();
      }
    },

    toggleMute() {
      const video = this.$refs.videoElement;
      if (!video) return;
      
      video.muted = !video.muted;
      this.isMuted = video.muted;
    },

    setVolume(event) {
      const video = this.$refs.videoElement;
      if (!video) return;
      
      const volume = parseInt(event.target.value, 10) / 100;
      video.volume = volume;
      this.volume = Math.round(volume * 100);
      
      // If volume is set above 0, unmute the video
      if (volume > 0 && video.muted) {
        video.muted = false;
        this.isMuted = false;
      } else if (volume === 0) {
        // If volume is set to 0, mute the video
        video.muted = true;
        this.isMuted = true;
      } else {
        // Update muted state based on video.muted
        this.isMuted = video.muted;
      }
    },

    seekTo(event) {
      const video = this.$refs.videoElement;
      if (!video || !this.duration) return;
      
      const progressBar = event.currentTarget;
      const rect = progressBar.getBoundingClientRect();
      const percent = (event.clientX - rect.left) / rect.width;
      const newTime = percent * this.duration;
      
      video.currentTime = newTime;
      this.currentTime = newTime;
    },

    toggleFullscreen() {
      const video = this.$refs.videoElement;
      const container = this.$el.querySelector('.video-container');
      if (!container) return;

      // Check current fullscreen state first
      const fullscreenElement = document.fullscreenElement || 
                                document.webkitFullscreenElement || 
                                document.mozFullScreenElement || 
                                document.msFullscreenElement;
      
      const currentlyFullscreen = !!(fullscreenElement === container || 
                                     fullscreenElement === video ||
                                     fullscreenElement === this.$el);

      if (!currentlyFullscreen) {
        // Enter fullscreen
        const enterFullscreen = () => {
          if (container.requestFullscreen) {
            container.requestFullscreen().catch(err => {
              console.error('Error entering fullscreen:', err);
            });
          } else if (container.webkitRequestFullscreen) {
            container.webkitRequestFullscreen();
          } else if (container.mozRequestFullScreen) {
            container.mozRequestFullScreen();
          } else if (container.msRequestFullscreen) {
            container.msRequestFullscreen();
          } else if (video && video.webkitEnterFullscreen) {
            video.webkitEnterFullscreen();
          }
        };
        
        enterFullscreen();
      } else {
        // Exit fullscreen
        if (document.exitFullscreen) {
          document.exitFullscreen().catch(err => {
            console.error('Error exiting fullscreen:', err);
          });
        } else if (document.webkitExitFullscreen) {
          document.webkitExitFullscreen();
        } else if (document.mozCancelFullScreen) {
          document.mozCancelFullScreen();
        } else if (document.msExitFullscreen) {
          document.msExitFullscreen();
        }
      }
    },

    resetControlsTimeout() {
      if (this.isPlaying && !this.isPaused) {
        this.showControls = true;
        clearTimeout(this.controlsTimeout);
        this.controlsTimeout = setTimeout(() => {
          this.showControls = false;
        }, 3000);
      }
    },

    formatTime(seconds) {
      if (!seconds || isNaN(seconds)) return '0:00';
      const mins = Math.floor(seconds / 60);
      const secs = Math.floor(seconds % 60);
      return `${mins}:${secs.toString().padStart(2, '0')}`;
    },

    handleQualityChange({ key }) {
      if (key === 'auto') {
        if (this.hls) {
          this.hls.currentLevel = -1; // Auto
          this.currentLevel = -1;
        }
      } else {
        const levelIndex = parseInt(key, 10);
        if (this.hls && levelIndex >= 0 && levelIndex < this.availableLevels.length) {
          this.hls.currentLevel = levelIndex;
          this.currentLevel = levelIndex;
        }
      }
    },

    getQualityLabel(level) {
      // Try to match level with renditions from props
      if (this.renditions && this.renditions.length > 0) {
        // Match by resolution
        const resolution = `${level.width}x${level.height}`;
        const matchingRendition = this.renditions.find(
          (r) => r.resolution === resolution
        );
        if (matchingRendition) {
          return matchingRendition.name.toUpperCase();
        }
      }
      
      // Fallback to resolution-based label
      if (level.height >= 1080) {
        return '1080p';
      } else if (level.height >= 720) {
        return '720p';
      } else if (level.height >= 480) {
        return '480p';
      }
      return `${level.height}p`;
    },

    setupFullscreenListeners() {
      const video = this.$refs.videoElement;
      const container = this.$el.querySelector('.video-container');
      if (!video || this._fullscreenListenersSetup) return;

      const handleFullscreenChange = () => {
        // Check if video container or video element is in fullscreen mode
        const fullscreenElement = document.fullscreenElement || 
                                  document.webkitFullscreenElement || 
                                  document.mozFullScreenElement || 
                                  document.msFullscreenElement;
        
        const isFullscreen = !!(
          fullscreenElement === container ||
          fullscreenElement === video ||
          fullscreenElement === this.$el ||
          (video && video.webkitDisplayingFullscreen)
        );
        
        // Sync state
        this.isFullscreen = isFullscreen;
        
        // Force update to ensure dropdown re-renders with correct state
        this.$forceUpdate();
      };

      // Listen for fullscreen events on document
      document.addEventListener('fullscreenchange', handleFullscreenChange);
      document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.addEventListener('mozfullscreenchange', handleFullscreenChange);
      document.addEventListener('MSFullscreenChange', handleFullscreenChange);

      // Also listen on video element for webkit-specific events
      video.addEventListener('webkitbeginfullscreen', handleFullscreenChange);
      video.addEventListener('webkitendfullscreen', handleFullscreenChange);

      // Store handlers for cleanup
      this._fullscreenHandlers = handleFullscreenChange;
      this._fullscreenVideoHandlers = {
        webkitbeginfullscreen: handleFullscreenChange,
        webkitendfullscreen: handleFullscreenChange,
      };
      
      // Mark as setup to prevent duplicates
      this._fullscreenListenersSetup = true;
      
      // Check initial state
      handleFullscreenChange();
    },

    removeFullscreenListeners() {
      const video = this.$refs.videoElement;
      
      if (this._fullscreenHandlers) {
        document.removeEventListener('fullscreenchange', this._fullscreenHandlers);
        document.removeEventListener('webkitfullscreenchange', this._fullscreenHandlers);
        document.removeEventListener('mozfullscreenchange', this._fullscreenHandlers);
        document.removeEventListener('MSFullscreenChange', this._fullscreenHandlers);
        this._fullscreenHandlers = null;
      }
      
      if (video && this._fullscreenVideoHandlers) {
        video.removeEventListener('webkitbeginfullscreen', this._fullscreenVideoHandlers.webkitbeginfullscreen);
        video.removeEventListener('webkitendfullscreen', this._fullscreenVideoHandlers.webkitendfullscreen);
        this._fullscreenVideoHandlers = null;
      }
      
      this._fullscreenListenersSetup = false;
    },

    checkFullscreenState() {
      const container = this.$el?.querySelector('.video-container');
      const video = this.$refs.videoElement;
      
      const fullscreenElement = document.fullscreenElement || 
                                document.webkitFullscreenElement || 
                                document.mozFullScreenElement || 
                                document.msFullscreenElement;
      
      const isFullscreen = !!(
        fullscreenElement === container ||
        fullscreenElement === video ||
        fullscreenElement === this.$el ||
        (video && video.webkitDisplayingFullscreen)
      );
      
      if (this.isFullscreen !== isFullscreen) {
        this.isFullscreen = isFullscreen;
        this.$forceUpdate();
      }
    },

    getPopupContainer(triggerNode) {
      // In fullscreen, check if we're in a fullscreen element
      const fullscreenElement = document.fullscreenElement || 
                                document.webkitFullscreenElement || 
                                document.mozFullScreenElement || 
                                document.msFullscreenElement;
      
      if (fullscreenElement) {
        // Attach to fullscreen element to ensure visibility
        return fullscreenElement;
      }
      
      // Otherwise attach to body
      return document.body;
    },
  },
};
</script>

<style scoped lang="scss">
.video-player-wrapper {
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  background: #000;
}

.video-thumbnail-container {
  position: relative;
  cursor: pointer;
  overflow: hidden;
  border-radius: 8px;
  background: #000;
  aspect-ratio: 16 / 9;

  &:hover {
    .play-button-overlay {
      opacity: 1;
      transform: scale(1.1);
    }
  }
}

.video-thumbnail {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.video-thumbnail-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.play-button-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.3);
  transition: all 0.3s ease;
  opacity: 0.8;

  .play-button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 80px;
    height: 80px;
    background: rgba(255, 255, 255, 0.2);
    border-radius: 50%;
    backdrop-filter: blur(10px);
    transition: transform 0.3s ease;
  }
}

.processing-badge,
.error-badge {
  position: absolute;
  bottom: 12px;
  left: 12px;
  right: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 12px;
  background: rgba(0, 0, 0, 0.7);
  color: #fff;
  border-radius: 4px;
  font-size: 12px;
  backdrop-filter: blur(10px);
}

.error-badge {
  background: rgba(255, 77, 79, 0.9);
}

.processing-badge-overlay {
  position: absolute;
  top: 10px;
  left: 10px;
  z-index: 20;
  background: rgba(0, 0, 0, 0.7);
  padding: 6px 12px;
  border-radius: 4px;
  backdrop-filter: blur(10px);
  
  ::v-deep .ant-badge {
    .ant-badge-status-dot {
      background-color: #1890ff;
    }
    .ant-badge-status-text {
      color: #fff;
      font-size: 12px;
      margin-left: 8px;
    }
  }
}

.video-container {
  position: relative;
  background: #000;
  border-radius: 8px;
  overflow: hidden;
}

.video-player {
  width: 100%;
  height: 100%;
  display: block;
  outline: none;
  cursor: pointer;
}

.close-button {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 10;
  background: rgba(0, 0, 0, 0.6);
  border: none;
  color: #fff;

  &:hover {
    background: rgba(0, 0, 0, 0.8);
    color: #fff;
  }
}

.video-loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.7);
  z-index: 5;
  color: #fff;
  pointer-events: none;
  
  p {
    color: #fff;
    margin: 0;
    font-size: 14px;
  }
}

/* Custom Video Controls */
.video-controls {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.8) 0%, transparent 100%);
  opacity: 0;
  transition: opacity 0.3s ease;
  pointer-events: none;
  z-index: 10;

  &.controls-visible {
    opacity: 1;
    pointer-events: all;
  }
}

.controls-progress {
  width: 100%;
  height: 6px;
  cursor: pointer;
  background: rgba(255, 255, 255, 0.2);
  position: relative;
  margin-bottom: 8px;

  .progress-bar {
    width: 100%;
    height: 100%;
    position: relative;
    background: rgba(255, 255, 255, 0.2);
  }

  .progress-filled {
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    background: #1890ff;
    transition: width 0.1s linear;
    z-index: 2;
  }

  .progress-buffer {
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    background: rgba(255, 255, 255, 0.4);
    z-index: 1;
  }
}

.controls-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  color: #fff;
}

.controls-left,
.controls-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.control-button {
  color: #fff !important;
  padding: 4px 8px !important;
  font-size: 18px;
  border: none !important;
  background: transparent !important;
  
  &:hover {
    color: #1890ff !important;
    background: rgba(255, 255, 255, 0.1) !important;
  }

  .anticon {
    font-size: 18px;
  }
}

.volume-control {
  display: flex;
  align-items: center;
  gap: 4px;
}

.volume-slider-wrapper {
  width: 0;
  overflow: hidden;
  transition: width 0.3s ease;
  
  .volume-control:hover & {
    width: 60px;
  }
}

.volume-slider {
  width: 60px;
  height: 4px;
  -webkit-appearance: none;
  appearance: none;
  background: rgba(255, 255, 255, 0.3);
  outline: none;
  border-radius: 2px;
  cursor: pointer;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 12px;
    height: 12px;
    background: #fff;
    border-radius: 50%;
    cursor: pointer;
  }

  &::-moz-range-thumb {
    width: 12px;
    height: 12px;
    background: #fff;
    border-radius: 50%;
    cursor: pointer;
    border: none;
  }
}

.time-display {
  font-size: 12px;
  color: #fff;
  margin-left: 8px;
  white-space: nowrap;
}

.quality-control {
  position: relative;
  z-index: 1000;
  
  .quality-button {
    .quality-label {
      font-size: 12px;
      margin-left: 4px;
    }
  }
}

// Ensure dropdown menu is visible in fullscreen
:fullscreen .quality-control,
:-webkit-full-screen .quality-control,
:-moz-full-screen .quality-control,
:-ms-fullscreen .quality-control {
  z-index: 2147483647 !important;
  
  ::v-deep .ant-dropdown {
    z-index: 2147483647 !important;
  }
  
  ::v-deep .ant-dropdown-menu {
    z-index: 2147483647 !important;
    position: fixed !important;
  }
}


.quality-button {
  background: rgba(0, 0, 0, 0.7);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: #fff;
  backdrop-filter: blur(10px);
  font-size: 12px;
  padding: 4px 12px;
  height: auto;
  line-height: 1.5;

  &:hover,
  &:focus {
    background: rgba(0, 0, 0, 0.9);
    border-color: rgba(255, 255, 255, 0.3);
    color: #fff;
  }
}

// Fullscreen styles
.video-container.is-fullscreen {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw !important;
  height: 100vh !important;
  z-index: 9999;
  background: #000;

  .video-player {
    width: 100%;
    height: 100%;
  }

  .video-controls {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
  }
}

// Global styles for fullscreen dropdown (not scoped)
::v-deep .quality-dropdown-fullscreen {
  z-index: 2147483647 !important;
  
  // Force dropdown to appear above the button in fullscreen
  .ant-dropdown-menu {
    z-index: 2147483647 !important;
    position: fixed !important;
    transform: translateY(-100%) !important;
    margin-bottom: 8px !important;
    margin-top: 0 !important;
  }
  
  // Ensure proper positioning for top placements
  &.ant-dropdown-placement-top,
  &.ant-dropdown-placement-topLeft,
  &.ant-dropdown-placement-topRight {
    .ant-dropdown-menu {
      transform: translateY(-100%) !important;
      margin-bottom: 8px !important;
    }
  }
  
  // Override bottom placement if it's being used
  &.ant-dropdown-placement-bottom,
  &.ant-dropdown-placement-bottomLeft,
  &.ant-dropdown-placement-bottomRight {
    .ant-dropdown-menu {
      transform: translateY(-100%) !important;
      margin-bottom: 8px !important;
      margin-top: 0 !important;
    }
  }
}

// Ensure dropdown is clickable and visible in fullscreen
:fullscreen .quality-control,
:-webkit-full-screen .quality-control,
:-moz-full-screen .quality-control,
:-ms-fullscreen .quality-control {
  pointer-events: auto !important;
  position: relative !important;
  z-index: 2147483647 !important;
  
  .control-button {
    pointer-events: auto !important;
    z-index: 2147483647 !important;
    position: relative !important;
  }
  
  ::v-deep .ant-dropdown {
    z-index: 2147483647 !important;
    pointer-events: auto !important;
  }
  
  ::v-deep .ant-dropdown-menu {
    z-index: 2147483647 !important;
    pointer-events: auto !important;
  }
}

.video-container.is-fullscreen .quality-control {
  pointer-events: auto !important;
  z-index: 2147483647 !important;
  
  .control-button {
    pointer-events: auto !important;
    z-index: 2147483647 !important;
  }
  
  ::v-deep .ant-dropdown {
    z-index: 2147483647 !important;
    pointer-events: auto !important;
  }
  
  ::v-deep .ant-dropdown-menu {
    z-index: 2147483647 !important;
    pointer-events: auto !important;
  }
}

.video-loading-container,
.video-error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;
  border-radius: 8px;
  color: #8c8c8c;
  text-align: center;
  padding: 20px;
  min-height: 140px;
}


</style>
